import stampit from 'stampit';
import Utils from './utils-mixin';



const PrivilegedMethods = stampit().init(
  function(){
    let mapObj;

    /**
     *
     * Get map or return null if no map was created
     *
     * @memberOf module:MapApi/Google/MapApiFactory
     * @function getMap
     * @instance
     *
     * @return {object|null} google.maps.Map object instance
     */
    this.getMap = function getMap() {
      return mapObj || null;
    };

    /**
     *
     * Sets google map object value to be returned with getMap
     *
     * @memberOf module:MapApi/Google/MapApiFactory
     * @function setMap
     * @instance
     *
     * @param {object} map google.maps.Map object instance
     *
     */
    this.setMap = function setMap(map) {
      mapObj = map;
    };
  }
);


const InitiateProps = stampit({

  /**
   *
   * Initializes google maps handler with needed data.
   * Is called automatically with options passed to
   * main factory method / create method
   *
   * @ignore
   *
   * @param {object} opt object for initializing, see export below for all option parameters.
   * @param {object} opt.data
   * @param {object} [opt.options={}] additional {@link https://developers.google.com/maps/documentation/javascript/3.exp/reference#MapOptions|map options}
   *
   * @return void
   */
  init: function init({data, options}, {instance, stamp, args}) {
    this.data = data;
    this.options = options;
  },

  properties: {
    data: {},
    options: {}
  }
});


const PublicMethods = stampit({

  methods: {
    /**
     *
     * Pans map to coordinates
     *
     * @memberOf module:MapApi/Google/MapApiFactory
     * @instance
     *
     * @param {number} lat
     * @param {number} lng
     *
     * @return {object} The instance on which this method was called.
     */
    panTo: function panTo(lat, lng) {
      let latLngObj = this.createLatLng(lat, lng);

      this.getMap().panTo(latLngObj);

      return this;
    },

    /**
     *
     * Gets center of map
     *
     * @memberOf module:MapApi/Google/MapApiFactory
     * @instance
     *
     * @return {object} google.maps.LatLng object instance
     */
    getCenter: function getCenter() {
      return this.getMap().getCenter();
    },

    /**
     *
     * Sets center of map
     *
     * @memberOf module:MapApi/Google/MapApiFactory
     * @instance
     *
     * @param {number} lat
     * @param {number} lng
     *
     * @return {object} The instance on which this method was called.
     */
    setCenter: function setCenter(lat, lng) {
      let latLngObj = this.createLatLng(lat, lng);

      this.getMap().setCenter(latLngObj);

      return this;
    },

    /**
     *
     * Adds map to DOM
     *
     * @memberOf module:MapApi/Google/MapApiFactory
     * @instance
     *
     * @return {object} The instance on which this method was called.
     */
    addMap: function addMap() {
      let latLng,
        mapOptions,
        map,
        mapDomEl,
        stylesObj,
        mapObj;

      mapOptions = this.getMapOptions();

      mapDomEl = document.getElementById(this.data.id);

      mapObj = new google.maps.Map(mapDomEl, mapOptions);

      this.setMap(mapObj);

      if (this.data.styles) {
        this.addMapStylesToMap();
      }

      return this;
    },

    /**
     *
     * Creates object of merged map options and returns it
     *
     * @memberOf module:MapApi/Google/MapApiFactory
     * @instance
     *
     * @return {object} map options object
     */
    getMapOptions: function getMapOptions() {
      let mapOptions,
        mapDataOptions,
        initialControlType,
        mapTypeControls = ['roadmap', 'satellite'],
        center = this.createLatLng(this.data.centerLat || 0, this.data.centerLng || 0),
        zoom = this.data.initialZoom ? Number(this.data.initialZoom) : 2,
        showZoomControl = Number(this.data.hideZoom) === 1  ? false : true,
        showStreetViewControl = Number(this.data.hideStreetView) === 1  ? false : true,
        showFullScreenControl = Number(this.data.hideFullScreen) === 1  ? false : true,
        showScaleControl = Number(this.data.hideScale) === 1  ? false : true,
        showRotateControl = Number(this.data.hideRotate) === 1  ? false : true,
        minZoom = this.data.minZoom ? Number(this.data.minZoom) : null,
        maxZoom = this.data.maxZoom ? Number(this.data.maxZoom) : null,
        gestureHandling = this.data.gestureHandling || 'cooperative';

      // Create array of map control types
      // from comma separated string
      if (this.data.controlTypes) {
        mapTypeControls = this.data.controlTypes.split(',').map(function(item) {
          return item.trim();
        });

        // If only one map type control option
        // exists, remove it to hide single control button
        if (mapTypeControls.length === 1) {
          mapTypeControls = [];
        }
      }

      // Check if a specific map type should be
      // active on initial render, otherwise choose
      // first in list
      if (this.data.controlInitType) {
        initialControlType = this.data.controlInitType;
      } else {
        initialControlType = mapTypeControls[0];
      }

      mapDataOptions = {
        center: center,
        zoom: zoom,
        minZoom: minZoom,
        maxZoom: maxZoom,
        mapTypeId: initialControlType,
        gestureHandling: gestureHandling,
        zoomControl: showZoomControl,
        streetViewControl: showStreetViewControl,
        fullscreenControl: showFullScreenControl,
        scaleControl: showScaleControl,
        rotateControl: showRotateControl,
        mapTypeControlOptions: {
          mapTypeIds: mapTypeControls
        }
      };

      // Merge options from data attributes with
      // any of the override options passed to init method
      mapOptions = Object.assign(mapDataOptions, this.options);

      return mapOptions;

    },

    /**
     *
     * Adds google.maps.StyledMapType to google maps object
     * and make style types selectable as map type
     *
     * @memberOf module:MapApi/Google/MapApiFactory
     * @instance
     *
     * @return void
     */
    addMapStylesToMap: function addMapStylesToMap() {
      let stylesObjArray = JSON.parse(this.data.styles),
        stylesLength = stylesObjArray.length,
        styledMapType,
        i,
        itemInLoop,
        map = this.getMap();

      for (i = 0; i < stylesLength; i++) {

        itemInLoop = stylesObjArray[i];

        styledMapType = new google.maps.StyledMapType(
          itemInLoop.style,
          {
            name: itemInLoop.name
          }
        );

        map.mapTypes.set('styled_map_' + itemInLoop.id, styledMapType);
      }
    },

    /**
     *
     * Resizes map for proper rendering,
     * fx. if map container changes size
     *
     * @memberOf module:MapApi/Google/MapApiFactory
     * @instance
     *
     * @return {object} The instance on which this method was called.
     */
    resize: function resize() {
      google.maps.event.trigger(this.getMap(), 'resize');

      return this;
    },

    /**
     *
     * Map on ready function, triggered on 'idle', which accepts
     * callback. Event listener is only called once
     *
     * @param {function} cb callback
     * @return {object} The instance on which this method was called.
     */
    onReady: function onReady(cb){
      let instance = this;

      google.maps.event.addListenerOnce(this.getMap(), 'idle', function onReadyCallback(){
        cb.call(instance);
      });

      return this;
    },

    /**
     *
     * Fit map bounds to array of items
     *
     * @param {object[]} items array of instances created with fx. {@link module:MapApi/Google/MarkerApiFactory|MapApi/Google/MarkerApiFactory}
     *
     * @return {object} The instance on which this method was called.
     */
    fitToItems: function fitToItems(items=[]){
      let bounds = new google.maps.LatLngBounds();

      items.forEach(function(item){
        if (!item.isHidden()) {
          bounds.extend(item.getPosition());
        }
      });

      this.getMap().fitBounds(bounds);

      return this;
    }
  }
});

const MapApiFactory = stampit(PrivilegedMethods, InitiateProps, PublicMethods, Utils);




/**
 *
 * Google maps api factory created using stampit
 *
 * @module MapApi/Google/MapApiFactory
 * @mixes MapApi/Google/UtilsMixin
 *
 * Initializes google maps handler with needed data.
 *
 * @param {object}              opt                                          object for initializing. Note that opt.data keys are based on data-attribute names
 *                                                                           and are not the same as the actual google map option keys. The actual keys can be used
 *                                                                           via opt.options
 * @param {object}              opt.data
 * @param {string}              opt.data.id                                  map dom container id
 * @param {string|number}       [opt.data.centerLat=0]                       default center latitude of map
 * @param {string|number}       [opt.data.centerLng=0]                       default center longitude of map
 * @param {string|number}       [opt.data.minZoom=null]                      min zoom value of map
 * @param {string|number}       [opt.data.maxZoom=null]                      max zoom value of map
 * @param {string|number}       [opt.data.initialZoom=2]                     initial zoom value of map
 * @param {bool|string|number}  [opt.data.hideZoom=false]                    hide zoom controls
 * @param {bool|string|number}  [opt.data.hideStreetView=false]              hide streetview control
 * @param {bool|string|number}  [opt.data.hideFullScreen=false]              hide fullscreen control
 * @param {bool|string|number}  [opt.data.hideScale=false]                   hide scale control when zoomed in
 * @param {bool|string|number}  [opt.data.hideRotate=false]                  hide rotate control when zoomed in
 * @param {string}              [opt.data.gestureHandling=cooperative]       gesture handling setting
 * @param {string}              [opt.data.controlTypes='roadmap,satellite']  comma separated list of available control types
 * @param {string}              [opt.data.controlInitType='roadmap']         which control type to use on page load. Default is first in list of controlTypes
 * @param {object}              [opt.options={}]                             additional {@link https://developers.google.com/maps/documentation/javascript/3.exp/reference#MapOptions|map options},
 *                                                                           will override any existing options which were set via opt.data
 *
 * @example
 * import MapApiFactory from './map-api/google/map-api-factory'
 *
 * // or MapApiFactory.create({data, options});
 * let mapHandler = MapApiFactory({
 *   data: {
 *     id: 'some-id',
 *     hideFullScreen: '1'
 *   },
 *   options: {
 *     fullscreenControl: true // overrides opt.data.hideFullScreen
 *   }
 * });
 *
 * mapHandler.addMap(); // adds map to DOM
 *
 */
export default MapApiFactory;



// WEBPACK FOOTER //
// ./src/frontend/js/map-api/google/map-api-factory.js